import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Checkbox } from '../ui/checkbox';
import { Plus, Edit, Trash2, Loader2, UtensilsCrossed } from 'lucide-react';
import { selectableItemsAPI, categoriesAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const SelectableItemsManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('plated');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dynamicBarCats, setDynamicBarCats] = useState([]);
  const [dynamicCats, setDynamicCats] = useState({});
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    type: 'plated',
    sub_category: 'main',
    description: '',
    price_per_person: '',
    has_upcharge: false,
    requires_attendant: false
  });

  const itemTypes = [
    { value: 'plated', label: 'Plated Dinner' },
    { value: 'hors_doeuvres', label: 'Hors d\'Oeuvres' },
    { value: 'late_night', label: 'Late Night' },
    { value: 'bar', label: 'Bar Packages' },
    { value: 'buffet', label: 'Buffet Items' },
    { value: 'addon', label: 'Add-ons' }
  ];

  const makeCats = (type, fetched, fallback) => {
    const d = (fetched || []).filter(c => c.type === type);
    return d.length > 0
      ? d.map(c => ({ value: c.name.toLowerCase().replace(/\s+/g, '_'), label: c.name }))
      : fallback;
  };

  const subCategories = {
    plated: makeCats('plated', dynamicCats.plated, [
      { value: 'salad', label: 'Salad' }, { value: 'appetizer', label: 'Appetizer' },
      { value: 'main', label: 'Main Course' }, { value: 'dessert', label: 'Dessert' },
      { value: 'beverage', label: 'Coffee & Tea' }
    ]),
    hors_doeuvres: makeCats('hors_doeuvres', dynamicCats.hors_doeuvres, [
      { value: 'hot', label: 'Hot' }, { value: 'cold', label: 'Cold' }, { value: 'favourite', label: 'Favourite' }
    ]),
    late_night: makeCats('late_night', dynamicCats.late_night, [
      { value: 'hot', label: 'Hot' }, { value: 'cold', label: 'Cold' }
    ]),
    bar: dynamicBarCats.length > 0
      ? dynamicBarCats.map(c => ({ value: c.name.toLowerCase().replace(/\s+/g, '_'), label: c.name }))
      : [
          { value: 'beer', label: 'Beer' }, { value: 'wine', label: 'Wine' },
          { value: 'spirits', label: 'Spirits' }, { value: 'cocktails', label: 'Cocktails' }
        ],
    buffet: makeCats('buffet', dynamicCats.buffet, [
      { value: 'protein', label: 'Protein' }, { value: 'side', label: 'Side Dish' }, { value: 'salad', label: 'Salad' }
    ]),
    addon: makeCats('addon', dynamicCats.addon, [
      { value: 'food', label: 'Food' }, { value: 'service', label: 'Service' }, { value: 'equipment', label: 'Equipment' }
    ])
  };

  useEffect(() => {
    fetchItems();
  }, [typeFilter]);

  // Fetch dynamic bar categories on mount so the user can manage them in Settings → Categories → Bar
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [bar, plated, hd, ln, buffet, addon] = await Promise.all([
          categoriesAPI.getByType('bar').catch(() => []),
          categoriesAPI.getByType('plated').catch(() => []),
          categoriesAPI.getByType('hors_doeuvres').catch(() => []),
          categoriesAPI.getByType('late_night').catch(() => []),
          categoriesAPI.getByType('buffet').catch(() => []),
          categoriesAPI.getByType('addon').catch(() => []),
        ]);
        if (!cancelled) {
          setDynamicBarCats(bar || []);
          setDynamicCats({ plated, hors_doeuvres: hd, late_night: ln, buffet, addon });
        }
      } catch (err) {
        console.warn('Categories fetch failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const data = await selectableItemsAPI.getAll(typeFilter);
      setItems(data);
    } catch (error) {
      toast({ title: 'Error loading items', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name || '',
        type: item.type || 'plated',
        sub_category: item.sub_category || 'main',
        description: item.description || '',
        price_per_person: item.price_per_person?.toString() || '',
        has_upcharge: item.has_upcharge || false,
        requires_attendant: item.requires_attendant || false
      });
    } else {
      setEditingItem(null);
      setFormData({
        name: '',
        type: typeFilter,
        sub_category: subCategories[typeFilter]?.[0]?.value || 'main',
        description: '',
        price_per_person: '',
        has_upcharge: false,
        requires_attendant: false
      });
    }
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast({ title: 'Please enter a name', variant: 'destructive' });
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...formData,
        price_per_person: formData.price_per_person ? parseFloat(formData.price_per_person) : null
      };

      if (editingItem) {
        await selectableItemsAPI.update(editingItem.id, payload);
        toast({ title: 'Item updated successfully!' });
      } else {
        await selectableItemsAPI.create(payload);
        toast({ title: 'Item created successfully!' });
      }
      setIsDialogOpen(false);
      fetchItems();
    } catch (error) {
      toast({ title: 'Error saving item', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;

    try {
      await selectableItemsAPI.delete(item.id);
      toast({ title: 'Item deleted successfully!' });
      fetchItems();
    } catch (error) {
      toast({ title: 'Error deleting item', variant: 'destructive' });
    }
  };

  // Group items by sub_category
  const groupedItems = items.reduce((acc, item) => {
    const cat = item.sub_category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const getSubCategoryLabel = (value) => {
    const cats = subCategories[typeFilter] || [];
    return cats.find(c => c.value === value)?.label || value;
  };

  return (
    <div className="space-y-6">
      {/* Header with Add Button */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-[#1e3a5f]">Selectable Items</h2>
          <p className="text-sm text-gray-600">Manage menu items that can be selected in contracts and BEOs</p>
        </div>
        <Button 
          onClick={() => handleOpenDialog()} 
          className="bg-[#c9a227] hover:bg-[#b8922d] text-white"
          data-testid="add-selectable-item-btn"
        >
          <Plus size={18} className="mr-2" />
          Add Item
        </Button>
      </div>

      {/* Type Filter Tabs */}
      <Tabs value={typeFilter} onValueChange={setTypeFilter}>
        <TabsList className="bg-white flex-wrap h-auto gap-1 p-1">
          {itemTypes.map(type => (
            <TabsTrigger 
              key={type.value} 
              value={type.value}
              className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white"
            >
              {type.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {itemTypes.map(type => (
          <TabsContent key={type.value} value={type.value} className="mt-4">
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="animate-spin text-[#1e3a5f]" size={32} />
              </div>
            ) : items.length === 0 ? (
              <Card className="bg-gray-50">
                <CardContent className="py-8 text-center">
                  <UtensilsCrossed className="mx-auto text-gray-400 mb-3" size={48} />
                  <p className="text-gray-600">No {type.label.toLowerCase()} items yet.</p>
                  <Button 
                    onClick={() => handleOpenDialog()} 
                    variant="outline" 
                    className="mt-3"
                  >
                    <Plus size={16} className="mr-2" />
                    Add your first item
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {Object.entries(groupedItems).map(([category, categoryItems]) => (
                  <Card key={category}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg text-[#1e3a5f]">
                        {getSubCategoryLabel(category)}
                        <span className="text-sm text-gray-500 font-normal ml-2">
                          ({categoryItems.length} items)
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {categoryItems.map(item => (
                          <div 
                            key={item.id} 
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border hover:bg-gray-100"
                          >
                            <div className="flex-1">
                              <p className="font-medium text-gray-800">{item.name}</p>
                              {item.description && (
                                <p className="text-xs text-gray-500 truncate">{item.description}</p>
                              )}
                              {item.price_per_person && (
                                <p className="text-xs text-green-600">${item.price_per_person}/pp</p>
                              )}
                            </div>
                            <div className="flex gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleOpenDialog(item)}
                                className="h-8 w-8 p-0"
                              >
                                <Edit size={14} />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleDelete(item)}
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a5f]">
              {editingItem ? 'Edit Item' : 'Add New Item'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Chicken Supreme"
                data-testid="item-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select 
                  value={formData.type} 
                  onValueChange={(v) => setFormData({ 
                    ...formData, 
                    type: v,
                    sub_category: subCategories[v]?.[0]?.value || ''
                  })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {itemTypes.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label>Category</Label>
                  {formData.type === 'bar' && (
                    <button
                      type="button"
                      onClick={async () => {
                        const name = window.prompt('New bar category name (e.g., Non-Alcoholic):');
                        if (!name || !name.trim()) return;
                        try {
                          const created = await categoriesAPI.create({
                            name: name.trim(),
                            type: 'bar',
                            sort_order: dynamicBarCats.length + 1,
                          });
                          const updated = await categoriesAPI.getByType('bar');
                          setDynamicBarCats(updated || []);
                          const newValue = created.name.toLowerCase().replace(/\s+/g, '_');
                          setFormData(f => ({ ...f, sub_category: newValue }));
                          toast({ title: `Added "${created.name}"` });
                        } catch (e) {
                          const detail = e.response?.data?.detail || e.message;
                          toast({
                            title: detail === 'Category already exists'
                              ? 'That category already exists'
                              : 'Error creating category',
                            description: detail === 'Category already exists'
                              ? 'Pick a different name, or use the existing one from the dropdown.'
                              : detail,
                            variant: 'destructive'
                          });
                        }
                      }}
                      data-testid="add-bar-category-inline"
                      className="text-xs text-[#c9a227] hover:text-[#1e3a5f] underline"
                    >
                      + New Category
                    </button>
                  )}
                </div>
                <Select 
                  value={formData.sub_category} 
                  onValueChange={(v) => setFormData({ ...formData, sub_category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(subCategories[formData.type] || []).map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Description (optional)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description..."
                rows={2}
              />
            </div>

            <div>
              <Label>Price Per Person (optional)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.price_per_person}
                onChange={(e) => setFormData({ ...formData, price_per_person: e.target.value })}
                placeholder="0.00"
              />
            </div>

            <div className="flex gap-6">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="has_upcharge"
                  checked={formData.has_upcharge}
                  onCheckedChange={(v) => setFormData({ ...formData, has_upcharge: v })}
                />
                <Label htmlFor="has_upcharge" className="text-sm cursor-pointer">
                  Has Upcharge
                </Label>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="requires_attendant"
                  checked={formData.requires_attendant}
                  onCheckedChange={(v) => setFormData({ ...formData, requires_attendant: v })}
                />
                <Label htmlFor="requires_attendant" className="text-sm cursor-pointer">
                  Requires Attendant
                </Label>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-[#1e3a5f] hover:bg-[#2d5a8f]"
              data-testid="save-item-btn"
            >
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              {editingItem ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SelectableItemsManager;
